import {
  DateRangePickerField,
  Form,
  NumericFormatField,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useAccountingTypeOptions } from '../../hooks';
import { AccountTitleFormSchema } from '../../models/account-title';
import { ACCOUNT_TITLE_CODE_LENGTH } from '../../utils';
import { getCodeMaxValue } from '../../utils/get-code-max-value';

type FormValues = z.infer<typeof AccountTitleFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const AccountTitleForm: FC<Props> = ({ initialValues, onSubmit }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(AccountTitleFormSchema),
  });
  const options = useAccountingTypeOptions({ type: 'special' });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        disableSeparator
        max={getCodeMaxValue(ACCOUNT_TITLE_CODE_LENGTH)}
        errors={errors}
        required
      />
      <TextInputField
        name="name"
        autocompleteKey="accountTitle.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />
      <SelectField
        name="accountingId"
        label={t('accounting-name')}
        control={control}
        options={options.data}
        errors={errors}
        required
      />
      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />
      <NumericFormatField
        name="sortOrder"
        label={t('display-sort-order')}
        control={control}
        errors={errors}
        required
        allowNegative={false}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
